import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { listUsers, type User } from '../api/users';
import { listProjects, type Project } from '../api/projects';
import { getUsageSummary, type UsageSummary } from '../api/usage';
import { getBedrockMetrics } from '../api/metrics';
import { getMe, type MeResponse } from '../api/me';

// ---------------------------------------------------------------------------
// User (non-admin) Dashboard
// ---------------------------------------------------------------------------

function UserDashboard({
  me,
  loading,
  onRefresh,
}: {
  me: MeResponse | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();

  const spend = me?.spend ?? {};
  const totalSpend = (spend as any).total_spend ?? (spend as any).spend ?? 0;
  const totalTokens = (spend as any).total_tokens ?? 0;
  const totalRequests = (spend as any).total_requests ?? 0;

  const formatCurrency = (val: number) =>
    `$${(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatNumber = (val: number) => (val ?? 0).toLocaleString();

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">My Dashboard</h1>
        <div className="view__actions">
          <button className="btn btn--secondary" onClick={onRefresh} disabled={loading} type="button">
            Refresh
          </button>
        </div>
      </div>

      {loading && !me && (
        <div className="view__empty">Loading your dashboard...</div>
      )}

      {/* Personal KPI Cards */}
      <div className="dashboard-kpis">
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {formatCurrency(totalSpend)}
          </span>
          <span className="dashboard-kpi__label">My Spend</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {formatNumber(totalTokens)}
          </span>
          <span className="dashboard-kpi__label">My Tokens</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {formatNumber(totalRequests)}
          </span>
          <span className="dashboard-kpi__label">My Requests</span>
        </div>
        {me?.profile?.maxBudget != null && (
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">
              {formatCurrency(me.profile.maxBudget)}
            </span>
            <span className="dashboard-kpi__label">Budget Limit</span>
          </div>
        )}
      </div>

      {/* Account Info */}
      <div className="dashboard-sections">
        <section className="dashboard-section">
          <h2 className="view__section-title">Account Information</h2>
          <div className="settings-card">
            <div className="settings-card__row">
              <span className="settings-card__label">Email</span>
              <span className="settings-card__value">{me?.email ?? '--'}</span>
            </div>
            <div className="settings-card__row">
              <span className="settings-card__label">Role</span>
              <span className="settings-card__value">
                <span className={`status-badge status-badge--${me?.profile?.role === 'admin' ? 'active' : 'pending'}`}>
                  {me?.profile?.role ?? 'user'}
                </span>
              </span>
            </div>
            <div className="settings-card__row">
              <span className="settings-card__label">Status</span>
              <span className="settings-card__value">
                <span className={`status-badge status-badge--${me?.profile?.status === 'active' ? 'active' : 'disabled'}`}>
                  {me?.profile?.status ?? 'unknown'}
                </span>
              </span>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="dashboard-section">
          <h2 className="view__section-title">Quick Links</h2>
          <div className="settings-card">
            <div
              className="settings-card__row settings-card__row--clickable"
              onClick={() => navigate('/admin/me')}
            >
              <span className="settings-card__label">My Profile</span>
              <span className="settings-card__value">View your full profile and usage details</span>
            </div>
            <div
              className="settings-card__row settings-card__row--clickable"
              onClick={() => navigate('/admin/setup')}
            >
              <span className="settings-card__label">Setup Guide</span>
              <span className="settings-card__value">Learn how to configure your API access</span>
            </div>
            <div
              className="settings-card__row settings-card__row--clickable"
              onClick={() => navigate('/admin/request-access')}
            >
              <span className="settings-card__label">Request Access</span>
              <span className="settings-card__value">Request elevated access or a role change</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Dashboard (existing behavior)
// ---------------------------------------------------------------------------

function AdminDashboard() {
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live counter state
  const [liveRequests, setLiveRequests] = useState<number | null>(null);
  const [liveModels, setLiveModels] = useState<number | null>(null);
  const [liveSpend, setLiveSpend] = useState<number | null>(null);
  const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }
      const [usersData, projectsData, usageData] = await Promise.all([
        listUsers(token).catch(() => [] as User[]),
        listProjects(token).catch(() => [] as Project[]),
        getUsageSummary(token).catch(() => null),
      ]);
      setUsers(usersData);
      setProjects(projectsData);
      setUsage(usageData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  // Live counters fetch
  const fetchLive = useCallback(async () => {
    try {
      const token = await getJwtToken();
      if (!token) return;

      const [metricsData, spendData] = await Promise.all([
        getBedrockMetrics(token, 0.083, 60).catch(() => null),
        getUsageSummary(token).catch(() => null),
      ]);

      // Count recent requests from the last 5 min metrics
      if (metricsData) {
        let totalInvocations = 0;
        let activeModelCount = 0;
        const summary = metricsData.summary ?? {};
        for (const modelId of Object.keys(summary)) {
          const s = (summary as any)[modelId];
          if (s?.invocations > 0) {
            totalInvocations += s.invocations;
            activeModelCount++;
          }
        }
        // Also count models with zero invocations in the period
        if (activeModelCount === 0) {
          activeModelCount = metricsData.models?.length ?? 0;
        }
        setLiveRequests(totalInvocations);
        setLiveModels(activeModelCount);
      }

      if (spendData) {
        setLiveSpend(spendData.totalSpend ?? 0);
      }
    } catch {
      // Silently fail — live counters are best-effort
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchLive();
    liveIntervalRef.current = setInterval(fetchLive, 15_000);
    return () => {
      if (liveIntervalRef.current) clearInterval(liveIntervalRef.current);
    };
  }, [fetchLive]);

  const activeUsers = users.filter((u) => u.status === 'active').length;
  const activeProjects = projects.filter((p) => p.enabled).length;

  const formatCurrency = (val: number) =>
    `$${(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatNumber = (val: number) => (val ?? 0).toLocaleString();

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Dashboard</h1>
        <div className="view__actions">
          <button className="btn btn--secondary" onClick={fetchData} disabled={loading} type="button">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && !usage && (
        <div className="view__empty">Loading dashboard data...</div>
      )}

      {/* Live Counters */}
      <div className="dashboard-live" style={{ marginBottom: 20 }}>
        <h2 className="view__section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="live-dot" />
          Live
        </h2>
        <div className="dashboard-kpis">
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">
              {liveRequests !== null ? liveRequests.toLocaleString() : '--'}
            </span>
            <span className="dashboard-kpi__label">Requests (5 min)</span>
          </div>
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">
              {liveModels !== null ? liveModels : '--'}
            </span>
            <span className="dashboard-kpi__label">Active Models</span>
          </div>
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">
              {liveSpend !== null
                ? `$${liveSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : '--'}
            </span>
            <span className="dashboard-kpi__label">Spend Today</span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-kpis">
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">{activeUsers}</span>
          <span className="dashboard-kpi__label">Active Users</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">{activeProjects}</span>
          <span className="dashboard-kpi__label">Active Projects</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {usage ? formatCurrency(usage.totalSpend) : '--'}
          </span>
          <span className="dashboard-kpi__label">Total Spend</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {usage ? formatNumber(usage.totalRequests) : '--'}
          </span>
          <span className="dashboard-kpi__label">Total Requests</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {usage ? formatNumber(usage.totalTokens) : '--'}
          </span>
          <span className="dashboard-kpi__label">Total Tokens</span>
        </div>
      </div>

      {/* Budget Alerts */}
      {(() => {
        const alertItems: { name: string; type: 'user' | 'project'; id: string; pct: number; spent: number; budget: number }[] = [];
        users.forEach((u) => {
          if (u.maxBudget != null && u.maxBudget > 0) {
            const spent = u.spend ?? 0;
            const pct = (spent / u.maxBudget) * 100;
            if (pct >= 80) {
              alertItems.push({ name: u.username, type: 'user', id: u.username, pct, spent, budget: u.maxBudget });
            }
          }
        });
        projects.forEach((p) => {
          if (p.maxBudget != null && p.maxBudget > 0) {
            const spent = p.spend ?? 0;
            const pct = (spent / p.maxBudget) * 100;
            if (pct >= 80) {
              alertItems.push({ name: p.name || p.projectId, type: 'project', id: p.projectId, pct, spent, budget: p.maxBudget });
            }
          }
        });
        alertItems.sort((a, b) => b.pct - a.pct);

        if (alertItems.length === 0) return null;

        return (
          <section className="budget-alerts">
            <h2 className="view__section-title">Budget Alerts</h2>
            <div className="settings-card">
              {alertItems.map((item) => {
                const tier = item.pct >= 100 ? 'red' : item.pct >= 90 ? 'red' : 'yellow';
                const clampedPct = Math.min(item.pct, 100);
                return (
                  <div
                    key={`${item.type}-${item.id}`}
                    className="budget-alert-item"
                    onClick={() => navigate(
                      item.type === 'user'
                        ? `/admin/users/${item.id}`
                        : `/admin/projects/${item.id}`
                    )}
                  >
                    <span className="budget-alert-item__name">
                      <span className={`status-badge status-badge--${item.type === 'user' ? 'pending' : 'active'}`}>
                        {item.type}
                      </span>{' '}
                      {item.name}
                    </span>
                    <div className="budget-alert-item__bar">
                      <div className="budget-bar">
                        <div
                          className={`budget-bar__fill budget-bar__fill--${tier}`}
                          style={{ width: `${clampedPct}%` }}
                        />
                      </div>
                    </div>
                    <span className={`budget-alert-item__pct budget-indicator__label--${tier}`}>
                      {item.pct.toFixed(0)}%
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      ${item.spent.toFixed(2)} / ${item.budget.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Quick Links */}
      <div className="dashboard-sections">
        <section className="dashboard-section">
          <h2 className="view__section-title">Recent Users</h2>
          {users.length === 0 ? (
            <div className="settings-card settings-card--empty">No users found</div>
          ) : (
            <div className="settings-card">
              {users.slice(0, 5).map((u) => (
                <div
                  key={u.username}
                  className="settings-card__row settings-card__row--clickable"
                  onClick={() => navigate(`/admin/users/${u.username}`)}
                >
                  <span className="settings-card__label">{u.username}</span>
                  <span className="settings-card__value">{u.email}</span>
                  <span className={`status-badge status-badge--${u.status === 'active' ? 'active' : 'disabled'}`}>
                    {u.status === 'active' ? 'Active' : 'Disabled'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {users.length > 5 && (
            <button
              className="btn btn--secondary"
              style={{ marginTop: 8 }}
              onClick={() => navigate('/admin/users')}
              type="button"
            >
              View All Users
            </button>
          )}
        </section>

        <section className="dashboard-section">
          <h2 className="view__section-title">Recent Projects</h2>
          {projects.length === 0 ? (
            <div className="settings-card settings-card--empty">No projects found</div>
          ) : (
            <div className="settings-card">
              {projects.slice(0, 5).map((p) => (
                <div
                  key={p.projectId}
                  className="settings-card__row settings-card__row--clickable"
                  onClick={() => navigate(`/admin/projects/${p.projectId}`)}
                >
                  <span className="settings-card__label">{p.projectId}</span>
                  <span className="settings-card__value">{p.name}</span>
                  <span className={`status-badge status-badge--${p.enabled ? 'active' : 'disabled'}`}>
                    {p.enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {projects.length > 5 && (
            <button
              className="btn btn--secondary"
              style={{ marginTop: 8 }}
              onClick={() => navigate('/admin/projects')}
              type="button"
            >
              View All Projects
            </button>
          )}
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Wrapper — detects admin vs user
// ---------------------------------------------------------------------------

export function Dashboard() {
  const { getJwtToken } = useAuth();

  const [me, setMe] = useState<MeResponse | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await getMe(token);
      setMe(data);
      setIsAdmin(data.profile?.role === 'admin');
    } catch {
      // If /me fails, default to user dashboard
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  if (isAdmin === null || loading) {
    return (
      <div>
        <div className="view__header">
          <h1 className="view__title">Dashboard</h1>
        </div>
        <div className="view__empty">Loading...</div>
      </div>
    );
  }

  if (isAdmin) {
    return <AdminDashboard />;
  }

  return <UserDashboard me={me} loading={loading} onRefresh={fetchMe} />;
}
